import React, { useState, useEffect, useRef } from "react";
import { Box, Typography, useMediaQuery, Fade, Grid } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

// Mock data to demonstrate the AI matching capabilities
const defaultShowcaseData = [
  {
    query: "a matching printed dupatta for my red dress",
    gender: "Women",
    products: [
      {
        id: 1,
        title: "Red Printed Dupatta",
        brand: "Diners",
        image_url: require("../../assets/images/Red Printed Dupatta_diners.jpg"),
      },
      {
        id: 2,
        title: "Printed Chiffon Dupatta",
        brand: "Sapphire",
        image_url: require("../../assets/images/Printed Chiffon Dupatta_Sapphire.webp"),
      },
      {
        id: 3,
        title: "Stitched Dupatta",
        brand: "Sana Safinaz",
        image_url: require("../../assets/images/Stitched Dupatta_SanaSafinaz.webp"),
      },
    ],
  },
  {
    query: "embroidered dresses under 10,000",
    gender: "Women",
    products: [
      {
        id: 4,
        title: "Embroidered Dress",
        brand: "ETHNC",
        image_url: require("../../assets/images/embroidered dress_ethnc.webp"),
      },
      {
        id: 5,
        title: "Embroidered Raw Silk Suit",
        brand: "Edenrobe",
        image_url: require("../../assets/images/Embroidered Raw Silk Suit_edenrobe.webp"),
      },
      {
        id: 6,
        title: "Embroidered Slub Stitched",
        brand: "Zeenwomen",
        image_url: require("../../assets/images/EMBROIDERED SLUB STITCHED SHIRT BOTTOM SUIT_zeenwomen.webp"),
      },
    ],
  },
  {
    query: "vacation look for women",
    gender: "Women",
    products: [
      {
        id: 7,
        title: "Indigo Palm Resort Shirt",
        brand: "Lama",
        image_url: require("../../assets/images/INDIGO PALM RESORT SHIRT_Lama.webp"),
      },
      {
        id: 8,
        title: "T-Shirt",
        brand: "ETHNC",
        image_url: require("../../assets/images/Tshirt_Ethnc.webp"),
      },
      {
        id: 9,
        title: "Cropped Printed Shirt",
        brand: "Outfitters",
        image_url: require("../../assets/images/Cropped Printed Shirt_outfitters.webp"),
      },
    ],
  },
  {
    query: "mother’s day gift ideas",
    gender: "Women",
    products: [
      {
        id: 10,
        title: "Hand Bag",
        brand: "Edenrobe",
        image_url: require("../../assets/images/Hand Bag_Edenrobe.webp"),
      },
      {
        id: 11,
        title: "Elegant Silver Tone Watch",
        brand: "Polkadots",
        image_url: require("../../assets/images/Elegant Silver Tone Watch_Polkadots.webp"),
      },
      {
        id: 12,
        title: "Mirage Perfume",
        brand: "Gulahmed Ideas",
        image_url: require("../../assets/images/Mirage Perfume_Gulahmed Ideas.webp"),
      },
    ],
  },
  {
    query: "formal shoes for men to match a blue suit",
    gender: "Men",
    products: [
      {
        id: 13,
        title: "Black Men Formal Shoes",
        brand: "Diners",
        image_url: require("../../assets/images/Black Men Formal Shoes_Diners.jpg"),
      },
      {
        id: 14,
        title: "Blue Formal Shoes",
        brand: "1st Step",
        image_url: require("../../assets/images/Blue Formal Shoes_1ststep.webp"),
      },
      {
        id: 15,
        title: "Leather Suit Shoes",
        brand: "Mango",
        image_url: require("../../assets/images/LEATHER_SUIT_SHOES mango.jpg"),
      },
    ],
  },
];

const DynamicShowcase = () => {
  const { homepageConfig } = useSelector((state) => state.uiContent);
  const showcaseData = homepageConfig?.dynamicShowcase;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [fade, setFade] = useState(true);
  const navigate = useNavigate();

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const [minHeight, setMinHeight] = useState('auto');
  const gridContentRef = useRef(null);

  useEffect(() => {
    setMinHeight('auto');
  }, [isMobile, isTablet]);

  useEffect(() => {
    if (gridContentRef.current) {
      const currentHeight = gridContentRef.current.offsetHeight;
      if (currentHeight > 0) {
        setMinHeight((prev) => {
          if (prev === 'auto') return `${currentHeight}px`;
          const prevVal = parseFloat(prev);
          return `${Math.max(prevVal, currentHeight)}px`;
        });
      }
    }
  }, [currentIndex, isMobile, isTablet]);

  useEffect(() => {
    if (!showcaseData || showcaseData.length === 0) return;

    const interval = setInterval(() => {
      setFade(false); // Start fading out

      setTimeout(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % showcaseData.length);
        setFade(true); // Fade back in with new data
      }, 600); // Wait for 600ms (100ms after the 500ms fade finishes) to guarantee opacity is 0

    }, 4500); // Change every 4.5 seconds

    return () => clearInterval(interval);
  }, [showcaseData]);

  if (!showcaseData || showcaseData.length === 0) {
    return null; // Don't show default dummy data, just wait for DB fetch
  }

  const currentItem = showcaseData[currentIndex];

  return (
    <Box
      sx={{
        maxWidth: { xs: "100%", md: "960px" },
        mx: "auto",
        px: isMobile ? 2.5 : 4,
        pt: isMobile ? 1.5 : 2,
        pb: 1,
      }}
    >
      <Box sx={{ mb: 2, textAlign: "center" }}>
        <Typography
          sx={{
            fontFamily: "Poppins-Medium",
            fontSize: "14px",
            color: "#888",
            textTransform: "uppercase",
            letterSpacing: "2px",
            mb: 1,
          }}
        >
          Handpik in Action
        </Typography>

        {/* Static 'Try Searching' text so it never blinks or shifts */}
        <Typography
          sx={{
            fontFamily: "'Lora', serif",
            fontSize: { xs: "24px", sm: "32px", md: "40px" },
            fontWeight: 500,
            color: "#111",
            lineHeight: 1.2,
            letterSpacing: "-0.02em",
            mb: { xs: 2.5, sm: 3 },
          }}
        >
          Try Searching
        </Typography>

        {/* Dynamic Rotating Query Button / Pill that is clearly clickable */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: { xs: "58px", sm: "64px", md: "70px" },
            px: 1,
          }}
        >
          <Fade in={fade} timeout={400}>
            <Box
              onClick={() => navigate('/search', { state: { search: currentItem.query, gender: currentItem.gender } })}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: { xs: 1, sm: 1.5 },
                backgroundColor: "#FFF4EE",
                border: "1.5px solid rgba(255, 107, 0, 0.35)",
                borderRadius: "100px",
                px: { xs: 2.5, sm: 3.5, md: 4 },
                py: { xs: 1, sm: 1.25, md: 1.5 },
                cursor: "pointer",
                maxWidth: "750px",
                boxShadow: "0 4px 20px rgba(255, 107, 0, 0.12)",
                position: "relative",
                overflow: "hidden",
                transition: "all 0.3s cubic-bezier(0.2, 0.8, 0.2, 1)",
                "@keyframes queryPulse": {
                  "0%, 100%": {
                    transform: "translateY(0px) scale(1)",
                    boxShadow: "0 4px 18px rgba(255, 107, 0, 0.12)",
                    borderColor: "rgba(255, 107, 0, 0.35)"
                  },
                  "50%": {
                    transform: "translateY(-4px) scale(1.03)",
                    boxShadow: "0 10px 28px rgba(255, 107, 0, 0.28)",
                    borderColor: "#FF6B00"
                  }
                },
                animation: "queryPulse 3.5s ease-in-out infinite",
                "&:hover": {
                  transform: "translateY(-5px) scale(1.04)",
                  backgroundColor: "#FFEFE5",
                  borderColor: "#FF6B00",
                  boxShadow: "0 12px 30px rgba(255, 107, 0, 0.32)",
                  "& .arrow-icon": {
                    transform: "translateX(4px)",
                  }
                },
                "&:active": {
                  transform: "scale(0.98)"
                },
                "@keyframes sweep": {
                  "0%, 15%": { left: "-100%" },
                  "45%": { left: "200%" },
                  "100%": { left: "200%" }
                },
                "&::after": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: "-100%",
                  width: "40%",
                  height: "100%",
                  background: "linear-gradient(to right, rgba(255,255,255,0) 0%, rgba(255,255,255,0.7) 50%, rgba(255,255,255,0) 100%)",
                  transform: "skewX(-25deg)",
                  animation: "sweep 4s infinite ease-in-out",
                  pointerEvents: "none",
                }
              }}
            >
              <Typography
                sx={{
                  fontFamily: "'Lora', serif",
                  fontSize: { xs: "15px", sm: "19px", md: "23px" },
                  fontStyle: "italic",
                  fontWeight: 600,
                  color: "#FF6B00",
                  lineHeight: 1.3,
                  textAlign: "center",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                "{currentItem.query}"
              </Typography>

              {/* Click prompt icon */}
              <Box
                className="arrow-icon"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: { xs: "24px", sm: "28px" },
                  height: { xs: "24px", sm: "28px" },
                  minWidth: { xs: "24px", sm: "28px" },
                  borderRadius: "50%",
                  backgroundColor: "#FF6B00",
                  color: "#FFF",
                  transition: "transform 0.2s ease",
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                  <polyline points="12 5 19 12 12 19"></polyline>
                </svg>
              </Box>
            </Box>
          </Fade>
        </Box>
      </Box>

      {/* Dynamic Results Grid */}
      <Box sx={{ position: "relative", minHeight }}>
        <Fade in={fade} timeout={500}>
          <Grid ref={gridContentRef} container spacing={isMobile ? 2 : 4} justifyContent="center">
            {currentItem.products.map((product, pIdx) => (
              <Grid item xs={4} sm={4} md={4} key={`${currentIndex}-${pIdx}`}>
                <Box
                  onClick={() => navigate('/search', { state: { search: currentItem.query, gender: currentItem.gender } })}
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    cursor: "pointer",
                    "&:hover .product-image-container": {
                      transform: "translateY(-6px)",
                      boxShadow: "0 16px 32px rgba(0,0,0,0.1)",
                    },
                    "&:hover .product-image": {
                      transform: "scale(1.05)",
                    },
                  }}
                >
                  <Box
                    className="product-image-container"
                    sx={{
                      width: "100%",
                      aspectRatio: { xs: "2/3", sm: "4/5" },
                      height: { xs: "auto", sm: "280px", md: "350px" },
                      borderRadius: "16px",
                      overflow: "hidden",
                      backgroundColor: "#F9F9F9",
                      transition: "all 0.5s cubic-bezier(0.165, 0.84, 0.44, 1)",
                      mb: 2.5,
                      position: "relative",
                    }}
                  >
                    <img
                      className="product-image"
                      src={product.image_url}
                      alt={product.title}
                      referrerPolicy="no-referrer"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        transition: "transform 0.7s cubic-bezier(0.165, 0.84, 0.44, 1)",
                      }}
                    />
                  </Box>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      sx={{
                        fontSize: "11px",
                        fontFamily: "Poppins-Medium",
                        fontWeight: 600,
                        color: "#999",
                        textTransform: "uppercase",
                        letterSpacing: "1px",
                        mb: 0.5,
                      }}
                    >
                      {product.brand}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: "12px", sm: "15px" },
                        fontWeight: 500,
                        color: "#111",
                        fontFamily: "Poppins-Medium",
                        display: "-webkit-box",
                        WebkitLineClamp: { xs: 2, sm: 1 },
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {product.title}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Fade>
      </Box>

      {/* Secretly preload all images in the background so transitions are instantaneous */}
      <Box sx={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden', opacity: 0 }}>
        {showcaseData.map((showcase, sIdx) =>
          showcase.products.map((product, pIdx) => (
            product.image_url && <img key={`${sIdx}-${pIdx}`} src={product.image_url} referrerPolicy="no-referrer" alt="preload" />
          ))
        )}
      </Box>
    </Box>
  );
};

export default DynamicShowcase;
